import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { IEvent, IUser } from "@/components/today/interfaces";
import type {
	TCalendarView,
	TEventColor,
	TEventCategory,
} from "@/components/today/types";

interface CalendarSettings {
	badgeVariant: "dot" | "colored";
	view: TCalendarView;
	use24HourFormat: boolean;
	agendaModeGroupBy: "date" | "color";
}

/** Sidebar-selected company: task/meeting events with companyId are shown only when their company is selected. */
export type CalendarSelectedCompanyId = number | null;

interface CalendarState {
	// Settings (persisted)
	settings: CalendarSettings;
	
	// UI State
	selectedDate: Date;
	selectedUserId: IUser["id"] | "all";
	selectedColors: TEventColor[];
	selectedCategory: TEventCategory;
	/** Synced from sidebar: events with companyId are shown only when companyId === selectedCompanyId */
	selectedCompanyId: CalendarSelectedCompanyId;

	// Data (set from props, not persisted)
	allEvents: IEvent[];
	filteredEvents: IEvent[];
	allUsers: IUser[];

	// Actions
	setView: (view: TCalendarView) => void;
	setBadgeVariant: (variant: "dot" | "colored") => void;
	toggleTimeFormat: () => void;
	setAgendaModeGroupBy: (groupBy: "date" | "color") => void;
	setSelectedDate: (date: Date | undefined) => void;
	setSelectedUserId: (userId: IUser["id"] | "all") => void;
	setSelectedCompanyId: (companyId: CalendarSelectedCompanyId) => void;
	filterEventsBySelectedColors: (color: TEventColor) => void;
	filterEventsByCategory: (category: TEventCategory) => void;
	filterEventsBySelectedUser: (userId: IUser["id"] | "all") => void;
	/** When companyId is passed, store's selectedCompanyId is updated with the event set (avoids flicker when switching company). */
	setEvents: (events: IEvent[], companyId?: CalendarSelectedCompanyId) => void;
	setUsers: (users: IUser[]) => void;
	addEvent: (event: IEvent) => void;
	updateEvent: (event: IEvent) => void;
	removeEvent: (eventId: number) => void;
	clearFilter: () => void;
}

function applyCompanyFilter(
	events: IEvent[],
	selectedCompanyId: CalendarSelectedCompanyId
): IEvent[] {
	if (selectedCompanyId == null) return events;
	return events.filter(
		(e) => e.companyId == null || e.companyId === selectedCompanyId
	);
}

function applyUserAndColorFilters(
	events: IEvent[],
	selectedUserId: IUser["id"] | "all",
	selectedColors: TEventColor[]
): IEvent[] {
	let result = events;
	if (selectedUserId !== "all") {
		result = result.filter((e) => e.user.id === selectedUserId);
	}
	if (selectedColors.length > 0) {
		result = result.filter((e) =>
			selectedColors.includes((e.color || "blue") as TEventColor)
		);
	}
	return result;
}

const DEFAULT_SETTINGS: CalendarSettings = {
	badgeVariant: "colored",
	view: "day",
	use24HourFormat: true,
	agendaModeGroupBy: "date",
};

/**
 * Zustand store for calendar state management
 * Replaces the previous React context-based calendar-context
 */
export const useCalendarStore = create<CalendarState>()(
	persist(
		(set, get) => ({
			// Initial state
			settings: DEFAULT_SETTINGS,
			selectedDate: new Date(),
			selectedUserId: "all",
			selectedColors: [],
			selectedCategory: "all",
			selectedCompanyId: null,
			allEvents: [],
			filteredEvents: [],
			allUsers: [],

			// Actions
			setView: (view: TCalendarView) => {
				set((state) => ({
					settings: { ...state.settings, view },
				}));
			},

			setBadgeVariant: (variant: "dot" | "colored") => {
				set((state) => ({
					settings: { ...state.settings, badgeVariant: variant },
				}));
			},

			toggleTimeFormat: () => {
				set((state) => ({
					settings: {
						...state.settings,
						use24HourFormat: !state.settings.use24HourFormat,
					},
				}));
			},

			setAgendaModeGroupBy: (groupBy: "date" | "color") => {
				set((state) => ({
					settings: { ...state.settings, agendaModeGroupBy: groupBy },
				}));
			},

			setSelectedDate: (date: Date | undefined) => {
				if (!date) return;
				set({ selectedDate: date });
			},

			setSelectedUserId: (userId: IUser["id"] | "all") => {
				set({ selectedUserId: userId });
				const state = get();
				const afterCompany = applyCompanyFilter(
					state.allEvents,
					state.selectedCompanyId
				);
				const filtered = applyUserAndColorFilters(
					afterCompany,
					userId,
					state.selectedColors
				);
				set({ filteredEvents: filtered });
			},

			setSelectedCompanyId: (companyId: CalendarSelectedCompanyId) => {
				set({ selectedCompanyId: companyId });
				const state = get();
				const afterCompany = applyCompanyFilter(
					state.allEvents,
					companyId
				);
				const filtered = applyUserAndColorFilters(
					afterCompany,
					state.selectedUserId,
					state.selectedColors
				);
				set({ filteredEvents: filtered });
			},

			filterEventsBySelectedColors: (color: TEventColor) => {
				const state = get();
				const isColorSelected = state.selectedColors.includes(color);
				const newColors = isColorSelected
					? state.selectedColors.filter((c) => c !== color)
					: [...state.selectedColors, color];

				const afterCompany = applyCompanyFilter(
					state.allEvents,
					state.selectedCompanyId
				);
				const filtered = applyUserAndColorFilters(
					afterCompany,
					state.selectedUserId,
					newColors
				);
				set({ filteredEvents: filtered, selectedColors: newColors });
			},

			filterEventsByCategory: (category: TEventCategory) => {
				set({ selectedCategory: category });
				// The actual filtering will be handled by data fetching in use-today.ts
				// This just updates the selected category state
			},

			filterEventsBySelectedUser: (userId: IUser["id"] | "all") => {
				get().setSelectedUserId(userId);
			},

			setEvents: (events: IEvent[], companyId?: CalendarSelectedCompanyId) => {
				set((state) => {
					const effectiveCompanyId =
						companyId !== undefined ? companyId : state.selectedCompanyId;
					const afterCompany = applyCompanyFilter(
						events,
						effectiveCompanyId
					);
					const filtered = applyUserAndColorFilters(
						afterCompany,
						state.selectedUserId,
						state.selectedColors
					);
					return {
						allEvents: events,
						filteredEvents: filtered,
						...(companyId !== undefined && {
							selectedCompanyId: companyId,
						}),
					};
				});
			},

			setUsers: (users: IUser[]) => {
				set({ allUsers: users });
			},

			addEvent: (event: IEvent) => {
				set((state) => {
					const nextAll = [...state.allEvents, event];
					const afterCompany = applyCompanyFilter(
						nextAll,
						state.selectedCompanyId
					);
					const filtered = applyUserAndColorFilters(
						afterCompany,
						state.selectedUserId,
						state.selectedColors
					);
					return { allEvents: nextAll, filteredEvents: filtered };
				});
			},

			updateEvent: (event: IEvent) => {
				const updated = {
					...event,
					startDate: new Date(event.startDate).toISOString(),
					endDate: new Date(event.endDate).toISOString(),
				};

				set((state) => {
					const nextAll = state.allEvents.map((e) =>
						e.id === event.id ? updated : e,
					);
					const afterCompany = applyCompanyFilter(
						nextAll,
						state.selectedCompanyId
					);
					const filtered = applyUserAndColorFilters(
						afterCompany,
						state.selectedUserId,
						state.selectedColors
					);
					return { allEvents: nextAll, filteredEvents: filtered };
				});
			},

			removeEvent: (eventId: number) => {
				set((state) => {
					const nextAll = state.allEvents.filter((e) => e.id !== eventId);
					const afterCompany = applyCompanyFilter(
						nextAll,
						state.selectedCompanyId
					);
					const filtered = applyUserAndColorFilters(
						afterCompany,
						state.selectedUserId,
						state.selectedColors
					);
					return { allEvents: nextAll, filteredEvents: filtered };
				});
			},

			clearFilter: () => {
				const state = get();
				const afterCompany = applyCompanyFilter(
					state.allEvents,
					state.selectedCompanyId
				);
				set({
					filteredEvents: afterCompany,
					selectedColors: [],
					selectedUserId: "all",
					selectedCategory: "all",
				});
			},
		}),
		{
			name: "calendar-settings", // localStorage key
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({
				settings: state.settings,
			}), // Only persist settings, not data
		},
	),
);

// Convenience hook that matches the old useCalendar API
// WARNING: This hook subscribes to the ENTIRE store and will cause re-renders on ANY state change
// For better performance, use useCalendarStore with specific selectors:
// Example: const selectedDate = useCalendarStore((state) => state.selectedDate)
export function useCalendar() {
	const store = useCalendarStore();
	return {
		selectedDate: store.selectedDate,
		setSelectedDate: store.setSelectedDate,
		selectedUserId: store.selectedUserId,
		setSelectedUserId: store.setSelectedUserId,
		badgeVariant: store.settings.badgeVariant,
		setBadgeVariant: store.setBadgeVariant,
		users: store.allUsers,
		selectedColors: store.selectedColors,
		filterEventsBySelectedColors: store.filterEventsBySelectedColors,
		selectedCategory: store.selectedCategory,
		filterEventsByCategory: store.filterEventsByCategory,
		filterEventsBySelectedUser: store.filterEventsBySelectedUser,
		events: store.filteredEvents,
		view: store.settings.view,
		use24HourFormat: store.settings.use24HourFormat,
		toggleTimeFormat: store.toggleTimeFormat,
		setView: store.setView,
		agendaModeGroupBy: store.settings.agendaModeGroupBy,
		setAgendaModeGroupBy: store.setAgendaModeGroupBy,
		addEvent: store.addEvent,
		updateEvent: store.updateEvent,
		removeEvent: store.removeEvent,
		clearFilter: store.clearFilter,
	};
}

// Performance-optimized selectors
// Use these instead of useCalendar() for better performance
export const calendarSelectors = {
	selectedCategory: (state: CalendarState) => state.selectedCategory,
	use24HourFormat: (state: CalendarState) => state.settings.use24HourFormat,
	removeEvent: (state: CalendarState) => state.removeEvent,
	addEvent: (state: CalendarState) => state.addEvent,
	updateEvent: (state: CalendarState) => state.updateEvent,
	view: (state: CalendarState) => state.settings.view,
	events: (state: CalendarState) => state.filteredEvents,
	users: (state: CalendarState) => state.allUsers,
} as const;

